import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (currentAttempt) => {
    const maxAttempts = 6;
    const pollInterval = 2000;

    if (currentAttempt >= maxAttempts) {
      setStatus('timeout');
      toast.error('Timeout - controlla la dashboard');
      return;
    }

    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Pagamento completato!');
        return;
      }
      setAttempts(currentAttempt + 1);
      setTimeout(() => pollPaymentStatus(currentAttempt + 1), pollInterval);
    } catch (e) {
      setStatus('error');
      toast.error('Errore controllo pagamento');
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }
    pollPaymentStatus(0);
  }, [sessionId, navigate, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6">
      <Card className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-lg" data-testid="payment-success-card">
        <CardContent className="p-12 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-[#EA580C] mx-auto mb-6 animate-spin" />
              <h2 className="text-2xl font-semibold text-[#0F172A] mb-2">Verifica Pagamento</h2>
              <p className="text-slate-600">Stiamo verificando il pagamento... (Tentativo {attempts + 1}/6)</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-[#0F172A] mb-2">Pagamento Completato!</h2>
              <p className="text-slate-600 mb-6">Il pagamento è stato elaborato con successo.</p>
              <Button data-testid="back-to-dashboard-btn" onClick={() => navigate('/dashboard')} className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-lg">
                Torna alla Dashboard
              </Button>
            </>
          )}
          {(status === 'error' || status === 'timeout') && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-[#0F172A] mb-2">Errore</h2>
              <p className="text-slate-600 mb-6">
                {status === 'timeout' ? 'Timeout nel controllo del pagamento. Verifica la dashboard.' : 'Errore.'}
              </p>
              <Button data-testid="back-to-dashboard-error-btn" onClick={() => navigate('/dashboard')} variant="outline" className="border-slate-300 rounded-lg">
                Torna alla Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
